import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations, LANGUAGES } from "@/i18n/translations";

const I18nContext = createContext({
    lang: "en",
    setLang: () => {},
    t: translations.en,
    languages: LANGUAGES,
});

function readLangFromUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        const q = params.get("lang");
        if (q && translations[q]) return q;
    } catch (_) {}
    return null;
}

function writeLangToUrl(next) {
    try {
        const url = new URL(window.location.href);
        url.searchParams.set("lang", next);
        window.history.replaceState({}, "", url.toString());
    } catch (_) {}
}

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        // Priority: URL > localStorage > browser > "en"
        const fromUrl = readLangFromUrl();
        if (fromUrl) return fromUrl;
        try {
            const saved = localStorage.getItem("davini.lang");
            if (saved && translations[saved]) return saved;
        } catch (_) {}
        const browser = (navigator.language || "en").slice(0, 2);
        return translations[browser] ? browser : "en";
    });

    // Sync URL + localStorage + <html lang> whenever language changes
    useEffect(() => {
        try {
            localStorage.setItem("davini.lang", lang);
        } catch (_) {}
        document.documentElement.lang = lang;
        writeLangToUrl(lang);
    }, [lang]);

    // React to back/forward navigation that changes ?lang=
    useEffect(() => {
        const onPop = () => {
            const fromUrl = readLangFromUrl();
            if (fromUrl && translations[fromUrl]) {
                setLangState((prev) => (prev === fromUrl ? prev : fromUrl));
            }
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    const setLang = useCallback((next) => {
        if (translations[next]) setLangState(next);
    }, []);

    const value = {
        lang,
        setLang,
        t: translations[lang] || translations.en,
        languages: LANGUAGES,
    };
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
